import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
    Box, Stack, Typography, Button, TextField, MenuItem, Avatar, Chip, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, CircularProgress,
    Drawer, IconButton, Divider,
} from '@mui/material';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import { useSelector } from 'react-redux';
import { selectUser, selectUserTypeId, USER_TYPE } from '../redux/slices/authSlice';
import http, { apiErrorMessage } from '../Api/http';
import {
    GetResignations, SubmitResignation, SetResignationStatus, DeleteResignation, GetUsersByUserType,
    GetMyResignation, WithdrawResignation, GetFinalSettlement, GetResignationSetting, SaveResignationSetting,
} from '../Api/Api';
import { PRIMARY } from '../theme';
import { card, solidBtn, ghostBtn, tonalBtn, field, PageHeader, EmptyState, ConfirmDialog } from '../components/uiKit';
import { initialsFromName as initials, avatarTone, fmtDate, isoFromDMY, inr } from '../utils/format';
import { Segmented } from '../components/LeaveAttendanceComps/punchUi';

const REASON_CATEGORIES = [
    'Better Opportunity', 'Personal Reasons', 'Relocation', 'Higher Studies', 'Health',
    'Work Environment', 'Compensation', 'Retirement', 'Termination', 'Other',
];

const STATUS_STYLE = {
    Pending: { label: 'Pending', color: '#B45309', bg: '#FFF7ED' },
    Approved: { label: 'Serving notice', color: '#0369A1', bg: '#E0F2FE' },
    Relieved: { label: 'Relieved', color: '#16A34A', bg: '#DCFCE7' },
    Withdrawn: { label: 'Withdrawn', color: '#64748B', bg: '#F1F5F9' },
};
const StatusPill = ({ status }) => {
    const s = STATUS_STYLE[status] || { label: status || '—', color: '#64748B', bg: '#F1F5F9' };
    return <Chip label={s.label} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: s.bg, color: s.color }} />;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const toApiDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = String(iso).slice(0, 10).split('-');
    return d ? `${d}-${m}-${y}` : iso;
};
// The old version sliced to 10 characters, which chopped the year off an
// already-formatted date ("11 Aug 2026" -> "11 Aug 202"). Parse what we get and
// render one consistent format instead.
const showDate = (v) => {
    if (!v) return '—';
    const s = String(v).trim();
    const t = new Date(isoFromDMY(s));
    return Number.isNaN(t.getTime()) ? s : fmtDate(t);
};
const daysBetween = (fromISO, toISO) => {
    if (!fromISO || !toISO) return 0;
    const a = new Date(fromISO), b = new Date(toISO);
    return Math.max(0, Math.round((b - a) / 86400000));
};

// Shared field-mapping for a resignation record, however the caller's screen
// (management list vs an employee's own) happens to key its response.
const mapResignationRow = (r) => ({
    id: r.id ?? r.Id,
    resignationId: r.id ?? r.Id ?? r.resignationId,
    employeeCode: r.employeeCode ?? r.EmployeeCode ?? '',
    employeeName: r.employeeName ?? r.EmployeeName ?? r.name ?? '',
    department: r.department ?? r.Department ?? '',
    designation: r.designation ?? r.Designation ?? '',
    resignationDate: r.resignationDate ?? r.ResignationDate ?? '',
    lastWorkingDay: r.lastWorkingDay ?? r.LastWorkingDay ?? '',
    noticeDays: r.noticeDays ?? r.NoticeDays ?? r.noticePeriodDays ?? null,
    daysRemaining: r.daysRemaining ?? null,
    reasonCategory: r.reasonCategory ?? r.ReasonCategory ?? '',
    reason: r.reason ?? r.Reason ?? '',
    status: r.status ?? r.Status ?? 'Pending',
    remarks: r.remarks ?? r.Remarks ?? '',
    approvedBy: r.approvedBy ?? null,
    approvedOn: r.approvedOn ?? null,
    relievedBy: r.relievedBy ?? null,
    relievedOn: r.relievedOn ?? null,
});

export default function ResignationsPage() {
    const user = useSelector(selectUser);
    const userTypeId = useSelector(selectUserTypeId);
    const isApprover = userTypeId === USER_TYPE.MASTER_ADMIN || userTypeId === USER_TYPE.ADMIN;
    const isMasterAdmin = userTypeId === USER_TYPE.MASTER_ADMIN;

    const [snack, setSnack] = useState(null);
    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    return (
        <Box sx={{ p: 2 }}>
            {isApprover
                ? <ApproverView notify={notify} isMasterAdmin={isMasterAdmin} userTypeId={userTypeId} user={user} />
                : <SelfRequestView user={user} notify={notify} />}

            <Snackbar
                open={!!snack}
                autoHideDuration={4000}
                onClose={() => setSnack(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snack ? <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)} sx={{ borderRadius: '8px' }}>{snack.msg}</Alert> : undefined}
            </Snackbar>
        </Box>
    );
}

function ApproverView({ notify, isMasterAdmin, userTypeId, user }) {
    const [rows, setRows] = useState([]);
    const [serverCards, setServerCards] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [action, setAction] = useState(null); // { row, to, kind, remarks }
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [adminCount, setAdminCount] = useState(null);
    const [resignOpen, setResignOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [settlementFor, setSettlementFor] = useState(null); // { resignationId, employeeName }
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Only an entity admin can action a resignation (approve/relieve/withdraw/
    // un-relieve/delete) — MasterAdmin reads across every entity but offboarding
    // itself is an entity-admin act.
    const canApprove = userTypeId === USER_TYPE.ADMIN;

    useEffect(() => {
        if (userTypeId !== USER_TYPE.ADMIN) return;
        http.get(GetUsersByUserType, { params: { id: USER_TYPE.ADMIN } })
            .then(({ data }) => {
                const d = data?.data;
                const list = Array.isArray(d) ? d : (d?.users || d?.items || d?.list || []);
                setAdminCount(list.length);
            })
            .catch(() => setAdminCount(null));
    }, [userTypeId]);

    const canAdminResign = userTypeId === USER_TYPE.ADMIN && (adminCount ?? 0) >= 2;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetResignations, {
                params: { status: statusFilter || undefined, search: search || undefined },
            });
            if (body?.error) throw new Error(body.message || 'Could not load resignations.');
            const d = body?.data ?? {};
            const list = Array.isArray(d) ? d : (d.resignations || d.items || d.list || []);
            setRows(list.map(mapResignationRow));
            setServerCards(d.cards || null);
            setLoadError('');
        } catch (err) {
            if (err?.response?.status === 404) { setRows([]); setServerCards(null); setLoadError(''); }
            else { setRows([]); setLoadError(apiErrorMessage(err, 'Could not load resignations.')); }
        } finally {
            setLoading(false);
        }
    }, [statusFilter, search]);

    useEffect(() => { load(); }, [load]);

    // The server's cards reflect every resignation regardless of the active
    // status filter; only fall back to a client count if it didn't send any
    // (e.g. an empty/erroring response).
    const cards = useMemo(() => {
        if (serverCards) {
            return {
                total: serverCards.total ?? 0,
                pending: serverCards.pending ?? 0,
                serving: serverCards.servingNotice ?? serverCards.serving ?? 0,
                relieved: serverCards.relieved ?? 0,
            };
        }
        const by = (s) => rows.filter((r) => r.status === s).length;
        return {
            total: rows.filter((r) => r.status !== 'Withdrawn').length,
            pending: by('Pending'),
            serving: by('Approved'),
            relieved: by('Relieved'),
        };
    }, [rows, serverCards]);

    const runStatus = async () => {
        const { row, to, kind, remarks } = action;
        setAction(null);
        setBusyId(row.id);
        try {
            const { data: body } = await http.put(SetResignationStatus, { id: row.id, status: to, remarks: remarks || '' });
            if (body?.error) throw new Error(body.message || 'Could not update the resignation.');
            const name = row.employeeName || row.employeeCode;
            if (kind === 'relieve') notify(`${name} relieved — marked inactive.`);
            else if (kind === 'unrelieve') notify(`${name} un-relieved — settlement reversed, employee reactivated.`);
            else if (kind === 'withdraw') notify('Resignation withdrawn — the employee stays active.');
            else notify('Resignation approved.');
            await load();
            setDetail(null);
            if (kind === 'relieve') setSettlementFor({ resignationId: row.id, employeeName: name });
        } catch (err) {
            notify(apiErrorMessage(err, isMasterAdmin
                ? 'Could not update. As Master Admin, pick the entity in the header first.'
                : 'Could not update the resignation.'), 'error');
        } finally { setBusyId(null); }
    };

    const removeRow = async (row) => {
        setConfirmDelete(null);
        setBusyId(row.id);
        try {
            const { data: body } = await http.delete(DeleteResignation, { params: { id: row.id } });
            if (body?.error) throw new Error(body.message || 'Could not delete the resignation.');
            notify('Resignation removed.');
            await load();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not delete. A relieved resignation must be withdrawn first.'), 'error');
        } finally { setBusyId(null); }
    };

    const FILTERS = ['', 'Pending', 'Approved', 'Relieved', 'Withdrawn'];
    const filterLabel = (f) => (f === '' ? 'All' : (STATUS_STYLE[f]?.label || f));

    // Search and the page actions sit at the right end of the breadcrumb row.
    const { setCrumbActions } = useOutletContext() || {};
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField
                size="small" placeholder="Search name / code / department"
                value={search} onChange={(e) => setSearch(e.target.value)}
                sx={{ ...field, width: { xs: '100%', sm: 260 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} /></InputAdornment> }}
            />
            {canApprove && (
                <IconButton onClick={() => setSettingsOpen(true)} sx={{ border: '1px solid #E5E7EB', borderRadius: '7px' }} title="Resignation settings">
                    <SettingsRoundedIcon sx={{ fontSize: 19, color: '#475569' }} />
                </IconButton>
            )}
            {canAdminResign && (
                <Button
                    startIcon={<LogoutRoundedIcon sx={{ fontSize: 18 }} />}
                    onClick={() => setResignOpen(true)}
                    sx={{ ...ghostBtn, height: 40, px: 2 }}
                >
                    Raise my resignation
                </Button>
            )}
            </Box>,
        );
        return () => setCrumbActions(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, search, canApprove, canAdminResign]);

    return (
        <>
            {isMasterAdmin && (
                <Box sx={{ ...card, p: 1.2, mb: 1.2, bgcolor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                    <Typography sx={{ fontSize: 12, color: '#1D4ED8' }}>
                        Viewing as Master Admin — this list is scoped to the entity selected in the header. Approving,
                        relieving and withdrawing is an entity-admin action; you can view records and final settlements
                        but not action them here.
                    </Typography>
                </Box>
            )}

            <Box sx={{ mb: 1.4, display: 'flex' }}>
                <Segmented
                    items={FILTERS.map((f) => ({ key: f || 'all', label: filterLabel(f) }))}
                    value={statusFilter || 'all'}
                    onChange={(k) => setStatusFilter(k === 'all' ? '' : k)}
                />
            </Box>

            {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={26} /></Box>
            ) : loadError ? (
                <Box sx={{ ...card, p: 0 }}><EmptyState icon={LogoutRoundedIcon} title="Couldn't load resignations" hint={loadError} /></Box>
            ) : rows.length === 0 ? (
                <Box sx={{ ...card, p: 0 }}><EmptyState icon={LogoutRoundedIcon} title="No resignations" hint="Resignations your staff raise will appear here for approval." /></Box>
            ) : (
                <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: RES_MIN_W }}>
                            <Box sx={{
                                display: 'grid', gridTemplateColumns: RES_COLS, alignItems: 'center', gap: 2,
                                px: RES_PAD_X, py: 1.2, bgcolor: '#FAFAFD', borderBottom: '1px solid #E6EAF1',
                                borderLeft: '3px solid transparent',
                            }}>
                                <HeadCell>Employee</HeadCell>
                                <HeadCell>Resigned on</HeadCell>
                                <HeadCell>Last working day</HeadCell>
                                <HeadCell>Notice</HeadCell>
                                <span />
                            </Box>

                            {rows.map((r) => (
                                <ResignationRow
                                    key={r.id}
                                    r={r}
                                    busy={busyId === r.id}
                                    selected={detail?.id === r.id}
                                    onOpen={setDetail}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
            )}

            <ResignationDetail
                r={detail}
                busy={busyId === detail?.id}
                readOnly={!canApprove}
                onClose={() => setDetail(null)}
                onApprove={() => setAction({ row: detail, to: 'Approved', kind: 'approve', remarks: '' })}
                onRelieve={() => setAction({ row: detail, to: 'Relieved', kind: 'relieve', remarks: '' })}
                onWithdraw={() => setAction({ row: detail, to: 'Withdrawn', kind: 'withdraw', remarks: '' })}
                onUnrelieve={() => setAction({ row: detail, to: 'Approved', kind: 'unrelieve', remarks: '' })}
                onDelete={() => setConfirmDelete(detail)}
                onViewSettlement={() => setSettlementFor({ resignationId: detail.id, employeeName: detail.employeeName || detail.employeeCode })}
            />

            <Dialog open={!!action} onClose={() => setAction(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                {action && (
                    <>
                        <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>
                            {action.kind === 'approve' ? 'Approve resignation'
                                : action.kind === 'relieve' ? 'Relieve employee'
                                    : action.kind === 'unrelieve' ? 'Un-relieve employee'
                                        : 'Withdraw resignation'}
                        </DialogTitle>
                        <DialogContent>
                            <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 1.5 }}>
                                {action.kind === 'approve' && `${action.row.employeeName || action.row.employeeCode} moves to serving notice.`}
                                {action.kind === 'relieve' && `${action.row.employeeName || action.row.employeeCode} will be marked INACTIVE and the Full & Final settlement will be generated automatically.`}
                                {action.kind === 'unrelieve' && `This reverses the Full & Final settlement, reopens any recovered advances, and reactivates ${action.row.employeeName || action.row.employeeCode}.`}
                                {action.kind === 'withdraw' && `The resignation is cancelled and ${action.row.employeeName || action.row.employeeCode} stays active.`}
                            </Typography>
                            <TextField
                                label="Remarks (optional)" size="small" fullWidth multiline minRows={2}
                                value={action.remarks} onChange={(e) => setAction((a) => ({ ...a, remarks: e.target.value }))}
                                sx={field}
                            />
                        </DialogContent>
                        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
                            <Button onClick={() => setAction(null)} sx={{ ...ghostBtn, height: 40 }}>Cancel</Button>
                            <Button onClick={runStatus} sx={{ ...solidBtn, height: 40, ...(action.kind === 'relieve' && { bgcolor: '#16A34A', '&:hover': { bgcolor: '#15803D' } }) }}>
                                {action.kind === 'approve' ? 'Approve' : action.kind === 'relieve' ? 'Relieve' : action.kind === 'unrelieve' ? 'Un-relieve' : 'Withdraw'}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <ConfirmDialog
                open={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={() => removeRow(confirmDelete)}
                title="Remove this resignation?"
                body="This soft-deletes the record. A relieved resignation must be withdrawn first."
                confirmLabel="Remove"
            />

            <Dialog open={resignOpen} onClose={() => setResignOpen(false)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>Raise my resignation</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mb: 1.5 }}>
                        Filed as {user?.loginId || '—'}. It goes to another admin for approval.
                    </Typography>
                    <ResignationForm
                        user={user} notify={notify}
                        onCancel={() => setResignOpen(false)}
                        onSubmitted={() => { setResignOpen(false); load(); }}
                    />
                </DialogContent>
            </Dialog>

            <SettlementDialog
                open={!!settlementFor}
                resignationId={settlementFor?.resignationId}
                employeeName={settlementFor?.employeeName}
                onClose={() => setSettlementFor(null)}
            />

            <ResignationSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} notify={notify} />
        </>
    );
}

const RES_COLS = 'minmax(210px, 1.5fr) 128px 128px 84px 34px';
const RES_MIN_W = 720;
const RES_PAD_X = 2;

const noticeOf = (r) => (r.noticeDays != null ? r.noticeDays : daysBetween(
    String(r.resignationDate).split('-').reverse().join('-'),
    String(r.lastWorkingDay).split('-').reverse().join('-'),
));

const daysRemainingOf = (r) => {
    if (r.daysRemaining != null) return r.daysRemaining;
    if (r.status !== 'Pending' && r.status !== 'Approved') return 0;
    return daysBetween(todayISO(), String(r.lastWorkingDay).split('-').reverse().join('-'));
};

const HeadCell = ({ children, align }) => (
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#5B7A94', textTransform: 'uppercase', letterSpacing: 0.6, textAlign: align || 'left' }}>
        {children}
    </Typography>
);

// One line per resignation so a screenful can be compared at a glance; the
// reason and the actions move into the detail drawer.
function ResignationRow({ r, busy, selected, onOpen }) {
    return (
        <Box
            onClick={() => onOpen(r)}
            sx={{
                display: 'grid', gridTemplateColumns: RES_COLS, alignItems: 'center', gap: 2,
                px: RES_PAD_X, py: 1.4, cursor: 'pointer',
                borderBottom: '1px solid #F3F8FC',
                borderLeft: `3px solid ${selected ? PRIMARY : 'transparent'}`,
                bgcolor: selected ? '#F7FBFE' : 'transparent',
                opacity: busy ? 0.55 : 1,
                transition: 'background-color .15s, opacity .15s',
                '&:hover': { bgcolor: selected ? '#F7FBFE' : '#F3F8FC' },
                '&:last-of-type': { borderBottom: 'none' },
            }}
        >
            <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', minWidth: 0 }}>
                <Avatar sx={{ width: 36, height: 36, ...avatarTone(r.employeeName || r.employeeCode), fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                    {initials(r.employeeName || r.employeeCode)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#111827', minWidth: 0 }} noWrap>{r.employeeName || r.employeeCode}</Typography>
                        <StatusPill status={r.status} />
                    </Stack>
                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }} noWrap>
                        {r.employeeCode}{r.designation ? ` · ${r.designation}` : ''}{r.department ? ` · ${r.department}` : ''}
                    </Typography>
                </Box>
            </Stack>

            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151', fontVariantNumeric: 'tabular-nums' }} noWrap>
                {showDate(r.resignationDate)}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151', fontVariantNumeric: 'tabular-nums' }} noWrap>
                {showDate(r.lastWorkingDay)}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151', fontVariantNumeric: 'tabular-nums' }} noWrap>
                {noticeOf(r)} days
            </Typography>

            <Stack sx={{ alignItems: 'flex-end' }}>
                {busy
                    ? <CircularProgress size={16} />
                    : <ChevronRightRoundedIcon sx={{ fontSize: 20, color: '#C4CBD6' }} />}
            </Stack>
        </Box>
    );
}

// Everything that doesn't belong in a scannable row: the full reason, the
// timeline, and the decisions.
function ResignationDetail({ r, busy, readOnly, onClose, onApprove, onRelieve, onWithdraw, onUnrelieve, onDelete, onViewSettlement }) {
    if (!r) return null;
    const notice = noticeOf(r);
    const remaining = daysRemainingOf(r);
    return (
        <Drawer
            anchor="right"
            open
            onClose={onClose}
            slotProps={{ paper: { sx: { width: { xs: '100%', sm: 440 }, borderTopLeftRadius: '14px', borderBottomLeftRadius: '14px' } } }}
        >
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1.4, px: 2.4, py: 2, borderBottom: '1px solid #F3F8FC' }}>
                <Avatar sx={{ width: 44, height: 44, ...avatarTone(r.employeeName || r.employeeCode), fontSize: 15, fontWeight: 700 }}>
                    {initials(r.employeeName || r.employeeCode)}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A' }} noWrap>{r.employeeName || r.employeeCode}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#98A0AE' }} noWrap>
                        {r.employeeCode}{r.designation ? ` · ${r.designation}` : ''}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
            </Stack>

            <Box sx={{ px: 2.4, py: 2, flex: 1, overflowY: 'auto' }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
                    <StatusPill status={r.status} />
                    {r.department && <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{r.department}</Typography>}
                </Stack>

                <Box sx={{ bgcolor: '#FAFAFC', border: '1px solid #F3F8FC', borderRadius: '9px', px: 1.6, py: 0.4, mb: 2 }}>
                    {[
                        ['Resigned on', showDate(r.resignationDate)],
                        ['Last working day', showDate(r.lastWorkingDay)],
                        ['Notice period', `${notice} days`],
                        ...((r.status === 'Pending' || r.status === 'Approved') ? [['Days remaining', `${remaining} days`]] : []),
                    ].map(([l, v], i) => (
                        <Stack key={l} direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 2, py: 1, borderTop: i === 0 ? 'none' : '1px solid #F3F8FC' }}>
                            <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{l}</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
                        </Stack>
                    ))}
                </Box>

                {(r.approvedBy || r.relievedBy) && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#5B7A94', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.8 }}>Trail</Typography>
                        <Stack spacing={0.6}>
                            {r.approvedBy && (
                                <Typography sx={{ fontSize: 12.5, color: '#475569' }}>
                                    Approved by <b>{r.approvedBy}</b>{r.approvedOn ? ` on ${showDate(r.approvedOn)}` : ''}
                                </Typography>
                            )}
                            {r.relievedBy && (
                                <Typography sx={{ fontSize: 12.5, color: '#475569' }}>
                                    Relieved by <b>{r.relievedBy}</b>{r.relievedOn ? ` on ${showDate(r.relievedOn)}` : ''}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                )}

                {(r.reasonCategory || r.reason) && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#5B7A94', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.8 }}>Reason</Typography>
                        <Stack direction="row" spacing={1.2} sx={{ px: 1.4, py: 1.2, borderRadius: '9px', bgcolor: '#FAFAFD', border: '1px solid #F1F3F8', alignItems: 'flex-start' }}>
                            <FormatQuoteRoundedIcon sx={{ fontSize: 16, color: '#C4CBD6', flexShrink: 0, mt: '1px' }} />
                            <Box sx={{ minWidth: 0 }}>
                                {r.reasonCategory && (
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#5B7A94', textTransform: 'uppercase', letterSpacing: 0.4 }}>{r.reasonCategory}</Typography>
                                )}
                                {r.reason && <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mt: 0.2 }}>{r.reason}</Typography>}
                            </Box>
                        </Stack>
                    </Box>
                )}

                {r.remarks && (
                    <Box>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#5B7A94', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.8 }}>Remarks</Typography>
                        <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{r.remarks}</Typography>
                    </Box>
                )}
            </Box>

            {r.status === 'Relieved' && (
                <Box sx={{ px: 2.4, pt: 0, pb: readOnly ? 2 : 0 }}>
                    <Button
                        fullWidth startIcon={<ReceiptLongRoundedIcon sx={{ fontSize: 17 }} />}
                        onClick={onViewSettlement}
                        sx={{ ...tonalBtn, height: 38, fontSize: 12.5 }}
                    >
                        View Final Settlement
                    </Button>
                </Box>
            )}

            {!readOnly && r.status !== 'Withdrawn' && (
                <Stack direction="row" spacing={0.8} sx={{ px: 2.4, py: 2, borderTop: '1px solid #F3F8FC', flexWrap: 'wrap', gap: 0.8 }}>
                    {r.status === 'Pending' && (
                        <>
                            <Button disabled={busy} startIcon={<CheckRoundedIcon sx={{ fontSize: 16 }} />} onClick={onApprove} sx={{ ...solidBtn, height: 36, fontSize: 12.5 }}>Approve</Button>
                            <Button disabled={busy} startIcon={<UndoRoundedIcon sx={{ fontSize: 16 }} />} onClick={onWithdraw} sx={{ ...ghostBtn, height: 36, fontSize: 12.5 }}>Decline</Button>
                        </>
                    )}
                    {r.status === 'Approved' && (
                        <>
                            <Button disabled={busy} startIcon={<HowToRegRoundedIcon sx={{ fontSize: 16 }} />} onClick={onRelieve} sx={{ ...solidBtn, height: 36, fontSize: 12.5, bgcolor: '#16A34A', '&:hover': { bgcolor: '#15803D' } }}>Relieve</Button>
                            <Button disabled={busy} startIcon={<UndoRoundedIcon sx={{ fontSize: 16 }} />} onClick={onWithdraw} sx={{ ...ghostBtn, height: 36, fontSize: 12.5 }}>Withdraw</Button>
                        </>
                    )}
                    {r.status === 'Relieved' && (
                        <Button disabled={busy} startIcon={<RestartAltRoundedIcon sx={{ fontSize: 16 }} />} onClick={onUnrelieve} sx={{ ...ghostBtn, height: 36, fontSize: 12.5 }}>Un-relieve</Button>
                    )}
                    {r.status !== 'Relieved' && (
                        <Button disabled={busy} onClick={onDelete} sx={{ ...ghostBtn, height: 36, fontSize: 12.5, ml: 'auto', color: '#DC2626', borderColor: '#FECACA', '&:hover': { bgcolor: '#FEF2F2', borderColor: '#FCA5A5' } }}>Delete</Button>
                    )}
                </Stack>
            )}
        </Drawer>
    );
}

// The payslip-style Full & Final settlement statement — read-only, fetched by
// resignation id. Only rendered lines are shown; nothing here hard-codes a
// specific earning/deduction key, since the backend only sends non-zero ones.
function SettlementDialog({ open, resignationId, employeeName, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !resignationId) return;
        let cancelled = false;
        setLoading(true); setError(''); setData(null);
        http.get(GetFinalSettlement, { params: { resignationId } })
            .then(({ data: body }) => {
                if (cancelled) return;
                if (body?.error) throw new Error(body.message || 'Could not load the settlement.');
                setData(body?.data || null);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.response?.status === 404
                    ? 'No Full & Final settlement found — it may have been reversed by an un-relieve.'
                    : apiErrorMessage(err, 'Could not load the settlement.'));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, resignationId]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
            <Stack direction="row" sx={{ alignItems: 'center', px: 2.5, py: 2, borderBottom: '1px solid #F3F8FC' }}>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800 }}>Full & Final Settlement</Typography>
                    <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{employeeName}</Typography>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
            </Stack>
            <DialogContent sx={{ p: 2.5 }}>
                {loading ? (
                    <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={24} /></Box>
                ) : error ? (
                    <EmptyState icon={ReceiptLongRoundedIcon} title="Settlement unavailable" hint={error} />
                ) : !data ? null : (
                    <Stack spacing={2}>
                        <Box sx={{ bgcolor: '#FAFAFC', border: '1px solid #F3F8FC', borderRadius: '9px', px: 1.6, py: 0.4 }}>
                            {[
                                ['Department', data.department],
                                ['Designation', data.designation],
                                ['Last working day', showDate(data.lastWorkingDay)],
                                ['Settlement month', data.settlementMonth],
                                ['Payable days', data.workingDaysInMonth != null ? `${data.payableDays} of ${data.workingDaysInMonth}` : data.payableDays],
                            ].filter(([, v]) => v != null && v !== '').map(([l, v], i) => (
                                <Stack key={l} direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 2, py: 0.9, borderTop: i === 0 ? 'none' : '1px solid #F3F8FC' }}>
                                    <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{l}</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{v}</Typography>
                                </Stack>
                            ))}
                        </Box>

                        {Array.isArray(data.earnings) && data.earnings.length > 0 && (
                            <SettlementTable title="Earnings" lines={data.earnings} total={data.grossEarned} totalLabel="Gross earned" />
                        )}
                        {Array.isArray(data.deductions) && data.deductions.length > 0 && (
                            <SettlementTable title="Deductions" lines={data.deductions} total={data.totalDeductions} totalLabel="Total deductions" />
                        )}

                        <Box sx={{
                            borderRadius: '9px', px: 1.8, py: 1.4,
                            bgcolor: data.employeeOwes ? '#FEF2F2' : '#F0FDF4',
                            border: `1px solid ${data.employeeOwes ? '#FECACA' : '#BBF7D0'}`,
                        }}>
                            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
                                    {data.employeeOwes && <WarningAmberRoundedIcon sx={{ fontSize: 18, color: '#DC2626' }} />}
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: data.employeeOwes ? '#B91C1C' : '#166534' }}>
                                        {data.employeeOwes ? 'Recoverable from employee' : 'Net Settlement'}
                                    </Typography>
                                </Stack>
                                <Typography sx={{ fontSize: 18, fontWeight: 800, color: data.employeeOwes ? '#DC2626' : '#15803D' }}>
                                    {inr(Math.abs(data.netSettlement || 0))}
                                </Typography>
                            </Stack>
                        </Box>

                        {(data.bankName || data.accountNumber) && (
                            <Box>
                                <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', mb: 0.8 }}>
                                    <AccountBalanceRoundedIcon sx={{ fontSize: 15, color: '#98A0AE' }} />
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#5B7A94', textTransform: 'uppercase', letterSpacing: 0.6 }}>Bank details</Typography>
                                </Stack>
                                <Typography sx={{ fontSize: 12.5, color: '#475569', lineHeight: 1.7 }}>
                                    {data.bankName}{data.branch ? ` · ${data.branch}` : ''}<br />
                                    A/C {data.accountNumber}{data.ifsc ? ` · IFSC ${data.ifsc}` : ''}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2 }}>
                <Button onClick={onClose} sx={{ ...ghostBtn, height: 38 }}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function SettlementTable({ title, lines, total, totalLabel }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#5B7A94', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.8 }}>{title}</Typography>
            <Box sx={{ border: '1px solid #F3F8FC', borderRadius: '9px', overflow: 'hidden' }}>
                {lines.map((l, i) => (
                    <Stack key={l.key || l.label || i} direction="row" sx={{ justifyContent: 'space-between', px: 1.6, py: 1, borderTop: i === 0 ? 'none' : '1px solid #F3F8FC' }}>
                        <Typography sx={{ fontSize: 13, color: '#475569' }}>{l.label}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>{inr(l.amount)}</Typography>
                    </Stack>
                ))}
                {total != null && (
                    <Stack direction="row" sx={{ justifyContent: 'space-between', px: 1.6, py: 1, bgcolor: '#FAFAFD', borderTop: '1px solid #F3F8FC' }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#5B7A94' }}>{totalLabel}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{inr(total)}</Typography>
                    </Stack>
                )}
            </Box>
        </Box>
    );
}

const SETTINGS_ROLES = [
    { id: USER_TYPE.EMPLOYEE, label: 'Employee' },
    { id: USER_TYPE.ADMIN, label: 'Admin' },
];

// Controls how long, after withdrawing a resignation, an employee must wait
// before submitting another. Small enough to live as a dialog off this page
// rather than its own settings screen.
function ResignationSettingsDialog({ open, onClose, notify }) {
    const [userTypeId, setUserTypeId] = useState(USER_TYPE.EMPLOYEE);
    const [days, setDays] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        http.get(GetResignationSetting, { params: { userTypeId } })
            .then(({ data: body }) => {
                if (body?.error) throw new Error(body.message);
                setDays(String(body?.data?.reapplyWaitingDays ?? 0));
            })
            .catch(() => setDays('0'))
            .finally(() => setLoading(false));
    }, [open, userTypeId]);

    const save = async () => {
        setSaving(true);
        try {
            const { data: body } = await http.put(SaveResignationSetting, {
                userTypeId, reapplyWaitingDays: Number(days) || 0,
            });
            if (body?.error) throw new Error(body.message || 'Could not save the setting.');
            notify('Resignation setting saved.');
            onClose();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save the setting.'), 'error');
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
            <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>Re-apply cooling period</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 12.5, color: '#6B7280', mb: 1.6 }}>
                    How long, after withdrawing a resignation, that role must wait before submitting another.
                </Typography>
                <Stack spacing={1.4}>
                    <TextField
                        select label="Role" size="small" fullWidth
                        value={userTypeId} onChange={(e) => setUserTypeId(Number(e.target.value))}
                        sx={field}
                    >
                        {SETTINGS_ROLES.map((r) => <MenuItem key={r.id} value={r.id} sx={{ fontSize: 13.5 }}>{r.label}</MenuItem>)}
                    </TextField>
                    <TextField
                        label="Waiting days" type="number" size="small" fullWidth
                        value={days} onChange={(e) => setDays(e.target.value)}
                        disabled={loading} sx={field}
                        slotProps={{ htmlInput: { min: 0 } }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
                <Button onClick={onClose} disabled={saving} sx={{ ...ghostBtn, height: 40 }}>Cancel</Button>
                <Button onClick={save} disabled={saving || loading} sx={{ ...solidBtn, height: 40 }}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ResignationForm({ user, notify, onSubmitted, onCancel }) {
    const EMPTY = { lastWorkingDay: '', reasonCategory: '', reason: '', remarks: '' };
    const [form, setForm] = useState(EMPTY);
    const [tried, setTried] = useState(false);
    const [saving, setSaving] = useState(false);

    const today = todayISO();
    const notice = daysBetween(today, form.lastWorkingDay);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const valid = form.lastWorkingDay && form.reasonCategory && form.reason.trim()
        && form.lastWorkingDay >= today;

    const submit = async () => {
        setTried(true);
        if (!valid) {
            notify(form.lastWorkingDay && form.lastWorkingDay < today ? 'The last working day cannot be in the past.'
                : 'Fill in all the required fields.', 'warning');
            return;
        }
        setSaving(true);
        try {
            // employee code + resignation date come from the token/server — don't send them.
            const { data: body } = await http.post(SubmitResignation, {
                lastWorkingDay: toApiDate(form.lastWorkingDay),
                reasonCategory: form.reasonCategory,
                reason: form.reason.trim(),
                remarks: form.remarks.trim(),
            });
            if (body?.error) throw new Error(body.message || 'Could not submit your resignation.');
            notify('Resignation submitted — pending approval.');
            onSubmitted?.();
        } catch (err) {
            const status = err?.response?.status;
            notify(status === 403
                ? 'Self-service resignation isn’t enabled for your account yet. Please ask your admin to raise it.'
                : apiErrorMessage(err, 'Could not submit your resignation.'), 'error');
        } finally { setSaving(false); }
    };

    return (
        <Stack spacing={1.6}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Chip
                    size="small"
                    label={form.lastWorkingDay
                        ? `Notice period: ${notice} day${notice === 1 ? '' : 's'}`
                        : 'Pick a last working day to see the notice period'}
                    sx={{
                        height: 24, fontSize: 11.5, fontWeight: 700,
                        bgcolor: form.lastWorkingDay ? '#E4F0F9' : '#F5F7FA',
                        color: form.lastWorkingDay ? '#0E4A75' : '#98A0AE',
                    }}
                />
            </Stack>

            {/* slotProps, not the removed InputLabelProps: without a shrunk label
                an empty date input renders its own dd-mm-yyyy placeholder
                underneath the label text. */}
            <TextField
                label="Last working day" type="date" size="small" fullWidth
                value={form.lastWorkingDay} onChange={set('lastWorkingDay')}
                error={tried && (!form.lastWorkingDay || form.lastWorkingDay < today)}
                sx={field}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: today } }}
            />

            <TextField
                select label="Reason category" size="small" fullWidth
                value={form.reasonCategory} onChange={set('reasonCategory')}
                error={tried && !form.reasonCategory} sx={field}
            >
                {REASON_CATEGORIES.map((c) => <MenuItem key={c} value={c} sx={{ fontSize: 13.5 }}>{c}</MenuItem>)}
            </TextField>

            <TextField
                label="Reason" size="small" fullWidth multiline minRows={3}
                value={form.reason} onChange={set('reason')}
                error={tried && !form.reason.trim()} sx={field}
                placeholder="Tell your admin why you’re resigning."
            />

            <TextField
                label="Remarks (optional)" size="small" fullWidth multiline minRows={2}
                value={form.remarks} onChange={set('remarks')}
                sx={field}
            />

            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                {onCancel && <Button onClick={onCancel} disabled={saving} sx={{ ...ghostBtn, height: 42, px: 2.5 }}>Cancel</Button>}
                <Button
                    onClick={submit} disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <LogoutRoundedIcon sx={{ fontSize: 18 }} />}
                    sx={{ ...solidBtn, height: 42, px: 3 }}
                >
                    {saving ? 'Submitting…' : 'Submit resignation'}
                </Button>
            </Stack>
        </Stack>
    );
}

// The employee's own "My Resignation" screen: fetches their current
// resignation (if any) and shows a status view with a Withdraw action, or
// falls back to the submit form when there's nothing active to show.
function SelfRequestView({ user, notify }) {
    const [loading, setLoading] = useState(true);
    const [mine, setMine] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [withdraw, setWithdraw] = useState(null); // { remarks }
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetMyResignation);
            if (body?.error) throw new Error(body.message || 'Could not load your resignation.');
            const d = body?.data;
            const list = Array.isArray(d) ? d : (Array.isArray(d?.resignations) ? d.resignations : (d ? [d] : []));
            const latest = list.map(mapResignationRow)[0] || null;
            setMine(latest && latest.status !== 'Withdrawn' ? latest : null);
            setLoadError('');
        } catch (err) {
            if (err?.response?.status === 404) { setMine(null); setLoadError(''); }
            else setLoadError(apiErrorMessage(err, 'Could not load your resignation.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const doWithdraw = async () => {
        setBusy(true);
        try {
            const { data: body } = await http.put(WithdrawResignation, { remarks: withdraw.remarks || '' });
            if (body?.error) throw new Error(body.message || 'Could not withdraw your resignation.');
            notify('Resignation withdrawn.');
            setWithdraw(null);
            await load();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not withdraw your resignation.'), 'error');
        } finally { setBusy(false); }
    };

    if (loading) {
        return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress size={26} /></Box>;
    }

    if (loadError) {
        return (
            <Box sx={{ maxWidth: 560, mx: 'auto' }}>
                <PageHeader title="My Resignation" subtitle="Your own resignation request and its status." />
                <Box sx={{ ...card, p: 0 }}><EmptyState icon={LogoutRoundedIcon} title="Couldn't load your resignation" hint={loadError} /></Box>
            </Box>
        );
    }

    if (!mine) {
        return (
            <Box sx={{ maxWidth: 620, mx: 'auto' }}>
                <PageHeader title="My Resignation" subtitle="Raise your resignation — it goes to your admin for approval." />
                <Box sx={{ ...card, p: { xs: 1.8, md: 2.4 } }}>
                    <ResignationForm user={user} notify={notify} onSubmitted={load} />
                </Box>
            </Box>
        );
    }

    const notice = noticeOf(mine);
    const remaining = daysRemainingOf(mine);
    const canWithdraw = mine.status === 'Pending' || mine.status === 'Approved';

    return (
        <Box sx={{ maxWidth: 620, mx: 'auto' }}>
            <PageHeader title="My Resignation" subtitle="Your own resignation request and its status." />
            <Box sx={{ ...card, p: { xs: 1.8, md: 2.4 } }}>
                <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: STATUS_STYLE[mine.status]?.bg || '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {mine.status === 'Relieved'
                            ? <DoneAllRoundedIcon sx={{ fontSize: 26, color: STATUS_STYLE[mine.status]?.color }} />
                            : <HourglassEmptyRoundedIcon sx={{ fontSize: 26, color: STATUS_STYLE[mine.status]?.color }} />}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A' }}>Your resignation</Typography>
                            <StatusPill status={mine.status} />
                        </Stack>
                        <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.3 }}>
                            {mine.status === 'Pending' && 'Awaiting your admin’s decision.'}
                            {mine.status === 'Approved' && 'Approved — you’re serving your notice period.'}
                            {mine.status === 'Relieved' && 'You have been relieved.'}
                        </Typography>
                    </Box>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ bgcolor: '#FAFAFC', border: '1px solid #F3F8FC', borderRadius: '9px', px: 1.6, py: 0.4, mb: 2 }}>
                    {[
                        ['Resigned on', showDate(mine.resignationDate)],
                        ['Last working day', showDate(mine.lastWorkingDay)],
                        ['Notice period', `${notice} days`],
                        ...(canWithdraw ? [['Days remaining', `${remaining} days`]] : []),
                    ].map(([l, v], i) => (
                        <Stack key={l} direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 2, py: 1, borderTop: i === 0 ? 'none' : '1px solid #F3F8FC' }}>
                            <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{l}</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
                        </Stack>
                    ))}
                </Box>

                {(mine.approvedBy || mine.relievedBy) && (
                    <Stack spacing={0.6} sx={{ mb: 2 }}>
                        {mine.approvedBy && (
                            <Typography sx={{ fontSize: 12.5, color: '#475569' }}>
                                Approved by <b>{mine.approvedBy}</b>{mine.approvedOn ? ` on ${showDate(mine.approvedOn)}` : ''}
                            </Typography>
                        )}
                        {mine.relievedBy && (
                            <Typography sx={{ fontSize: 12.5, color: '#475569' }}>
                                Relieved by <b>{mine.relievedBy}</b>{mine.relievedOn ? ` on ${showDate(mine.relievedOn)}` : ''}
                            </Typography>
                        )}
                    </Stack>
                )}

                {(mine.reasonCategory || mine.reason) && (
                    <Box sx={{ mb: canWithdraw ? 2 : 0 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#5B7A94', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.8 }}>Your reason</Typography>
                        <Stack direction="row" spacing={1.2} sx={{ px: 1.4, py: 1.2, borderRadius: '9px', bgcolor: '#FAFAFD', border: '1px solid #F1F3F8', alignItems: 'flex-start' }}>
                            <FormatQuoteRoundedIcon sx={{ fontSize: 16, color: '#C4CBD6', flexShrink: 0, mt: '1px' }} />
                            <Box sx={{ minWidth: 0 }}>
                                {mine.reasonCategory && (
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#5B7A94', textTransform: 'uppercase', letterSpacing: 0.4 }}>{mine.reasonCategory}</Typography>
                                )}
                                {mine.reason && <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mt: 0.2 }}>{mine.reason}</Typography>}
                            </Box>
                        </Stack>
                    </Box>
                )}

                {canWithdraw && (
                    <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
                        <Button
                            startIcon={<UndoRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setWithdraw({ remarks: '' })}
                            sx={{ ...ghostBtn, height: 38, fontSize: 12.5 }}
                        >
                            Withdraw
                        </Button>
                    </Stack>
                )}
            </Box>

            <Dialog open={!!withdraw} onClose={() => setWithdraw(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>Withdraw your resignation?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 1.5 }}>
                        This cancels your resignation and you stay active. A waiting period may apply before you can
                        submit another.
                    </Typography>
                    <TextField
                        label="Remarks (optional)" size="small" fullWidth multiline minRows={2}
                        value={withdraw?.remarks || ''} onChange={(e) => setWithdraw((w) => ({ ...w, remarks: e.target.value }))}
                        sx={field}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
                    <Button onClick={() => setWithdraw(null)} disabled={busy} sx={{ ...ghostBtn, height: 40 }}>Cancel</Button>
                    <Button onClick={doWithdraw} disabled={busy} sx={{ ...solidBtn, height: 40 }}>
                        {busy ? 'Withdrawing…' : 'Withdraw'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
